import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { of as observableOf, throwError, Observable, combineLatest, EMPTY } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, takeUntil, map } from 'rxjs/operators';

import moment from 'moment';

import { CodeValueService, CommonService, DialogService, NavigationService } from '@common';
import { PhxDialogComponent } from '@common/components/phx-dialog/phx-dialog.component';
import { PhxDialogComponentConfigModel } from '@common/components/phx-dialog/phx-dialog.component.model';
import { FboMonolithAtsFieldMappingDataService } from '@common/data-services/fbo-monolith-ats-field-mapping-data/fbo-monolith-ats-field-mapping-data.service';
import { OrganizationDataService } from '@common/data-services/organization-data/organization-data.service';
import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { ICommonListsItem } from '@common/lists';
import { CommonListsObservableService } from '@common/lists/lists.observable.service';
import { UserProfileWorkerListItem } from '@common/lists/models/user-profile-worker-list-item.model';
import { CodeValue, CodeValueGroups, DialogResultType, PhxConstants } from '@common/model';
import { ToolTipField } from '@common/model/phx-select-box-w-tooltip';
import { ToTypedFormGroup } from '@common/model/to-typed-form-group.model';
import { ApplicationTransactionSystemDto, AtsPlacementDto } from '@workorder/models/dto/application-transaction-system.dto';

import { WorkorderService } from '../../services';
import { getValidationSummary } from '../../shared/format-workorder-create-helper';
import { filterWorkOrderTemplates, filterWorkOrderWorkers } from '../../shared/workorder-template-filter.helper';

interface CreateFromAtsForm {
    OrganizationIdClient: number;
    UserProfileIdWorker: number;
    LineOfBusinessId: number;
    TemplateId: number;
}

@Component({
  selector: 'app-assignment-create',
  templateUrl: './assignment-create.component.html',
  styleUrls: ['./assignment-create.component.less'],
  standalone: false
})
export class AssignmentCreateComponent extends BaseComponentOnDestroy implements OnInit {
    @ViewChild(PhxDialogComponent, { static: true })
    phxDialogComponent: PhxDialogComponent;

    readonly codeValueGroups = CodeValueGroups;
    readonly phxConstants = PhxConstants;

    // These fields are not displayed by default, but can be shown by clicking the "Show all fields" button
    private readonly extraFields: Array<keyof AtsPlacementDto> = [
        'JobTitle',
        'JobFunction',
        'Hours',
        'ClientWorksiteSubdivision',
        'WorkerLocationSubdivision',
        'TimesheetMethodologyId',
        'TimesheetClientApprover',
        'TimesheetInternalApprover',
        'BillingRecipient',
        'ReferenceContact',
        'BillingCurrency',
        'PaymentCurrency'
    ] as const;

    get hasExtraFields(): boolean {
        if (!this.atsDto?.AtsPlacement) {return false;}
        return this.extraFields.some(field => this.atsDto.AtsPlacement[field]);
    }

    sourceId: number;
    placementId: number;

    showAllAtsFields = false;

    public phxDialogComponentConfigModel: PhxDialogComponentConfigModel = null;
    atsDto: ApplicationTransactionSystemDto;
    inputFormGroup: FormGroup<ToTypedFormGroup<CreateFromAtsForm>>;
    listProfileType: Array<CodeValue>;
    listOrganizationClient: Array<ICommonListsItem>;
    listWorkOrderTemplates: any;
    isRegularPlacement: boolean;
    atsOwnerLabel: string;
    listFilteredWorkOrderTemplates: Array<any> = [];
    listUserProfileWorker: any;
    workOrderCreateInProgress = false;
    ValidationMessages: any;
    private allUserProfileWorkers: UserProfileWorkerListItem[] = [];
    selectedProfileWorker: any;
    selectedProfileWorkerUrlLink: string;
    selectedTemplate: any;
    tooltipFields: Array<ToolTipField> = [
        { label: 'Email', value: 'PrimaryEmail' },
        { label: 'Organization', value: 'OrganizationName' },
        { label: 'Update Date', value: 'LastModifiedDatetime' }
    ];

    notFoundInAts = 'Not found in ATS';
    profileInactiveWarning = '[Profile is inactive]';
    entityNotFoundWarning = 'Unable to find in FBO';
    allowedStatusesForImport: string[] = [];

    constructor(
        private router: Router,
        public commonService: CommonService,
        private navigationService: NavigationService,
        private codeValueService: CodeValueService,
        private workorderService: WorkorderService,
        private listObservableService: CommonListsObservableService,
        private activatedRoute: ActivatedRoute,
        private dialogService: DialogService,
        private organizationDataService: OrganizationDataService,
        private atsFieldMappingService: FboMonolithAtsFieldMappingDataService
    ) {
        super();
    }

    ngOnInit() {
        this.navigationService.setTitle('workorder-new', ['Create from ATS']);

        this.getCodeValueListsStatic();
        this.getTemplatesByEntityTypeId();
        
        // First load allowed statuses, then setup page
        this.atsFieldMappingService.getAllowedStatusesForImport()
            .pipe(
                takeUntil(this.isDestroyed$),
                switchMap(allowedStatuses => {
                    this.allowedStatusesForImport = allowedStatuses;
                    return combineLatest([this.getListUserProfileWorker(), this.setupPage()]);
                })
            )
            .subscribe(
                ([listUserProfileWorker, atsDto]) => {
                    this.allUserProfileWorkers = listUserProfileWorker;
                    this.updateFilteredWorkers();

                    if (atsDto.SuggestedUserProfileIdWorker) {
                        this.setselectedProfileWorker(atsDto.SuggestedUserProfileIdWorker);
                    }
                }
            );
    }

    shouldShowField(field: keyof AtsPlacementDto): boolean {
        if (!this.extraFields.includes(field)) {
            return true;
        }
        
        // Only show extra fields if they are visible and have a value
        return this.showAllAtsFields && !!this.atsDto?.AtsPlacement?.[field];
    }

    setupPage(): Observable<ApplicationTransactionSystemDto> {
        return this.activatedRoute.params.pipe(            
            takeUntil(this.isDestroyed$),
            switchMap((routerParams: Params) => {
                this.sourceId = Number(routerParams.atssourceId);
                this.placementId = routerParams.atsplacementId ? Number(routerParams.atsplacementId) : 0;

                this.isRegularPlacement = false;
                this.atsOwnerLabel = 'Owner';
                return this.sourceId && this.placementId
                    ? this.workorderService.getAts(this.sourceId, this.placementId)
                    : observableOf(null);
            }),
            catchError((e: HttpErrorResponse) => {
                if (e.status === 500 && e.statusText === 'BullhornConnectivityException') {
                    this.navigateOnError(e.statusText);
                } else if (e.status === 500 && e.statusText === 'BullhornDataException') {
                    this.navigateOnError('Unable to create Work Order - ' + e.error);
                } else {
                    this.navigateOnError(`Unable to create Work Order - ${e.error.Message} - ${e.error.Description}`);
                }

                return throwError(() => e);
            }),
            switchMap(atsDto => {
                if (!atsDto?.AtsPlacement?.Id) {
                    this.navigateOnError('AtsPlacementNotFound');
                    return EMPTY;
                } else if (atsDto && !this.allowedStatusesForImport.includes(atsDto.AtsPlacement.Status)) {
                    const allowedStatusesText = this.allowedStatusesForImport.join(', ');
                    this.navigateOnError(`AtsPlacementNotApproved|${allowedStatusesText}`);
                    return EMPTY;
                } else {
                    this.atsDto = atsDto;
                    this.onInitSetup(this.atsDto);
                    return observableOf(this.atsDto);
                }
            })
        );
    }

    navigateOnError(errorCode: string) {
        this.router.navigate(['next', 'workorder', 'createsetup', 'importfromats',
            1,
            'atssource',
            this.sourceId,
            'atsplacement',
            this.placementId
        ], { queryParams: { errorCode: `${errorCode}` } });
    }

    validateLOBAndSubmit() {
        if (this.inputFormGroup.value.LineOfBusinessId === this.phxConstants.LineOfBusiness.PermPlacement) {
          const errMessage = 'Cannot create Work Order - ATS Placement Id is a Perm Placement';
          this.navigateOnError(errMessage);
          return;
        }
    
        this.organizationDataService.getClientRoleSelectedLOBCodes(this.inputFormGroup.value.OrganizationIdClient)
        .subscribe(async (selectedLOBCodes: string[]) => {
            const currentLOB = this.codeValueService.getCodeValues(this.codeValueGroups.LineOfBusiness, true).find(lob => this.inputFormGroup.value.LineOfBusinessId === lob.id);
            const doSelectedLOBCodesIncludeCurrentLOBCode = selectedLOBCodes.includes(currentLOB?.code);
      
            if (!doSelectedLOBCodesIncludeCurrentLOBCode) {
              const errMessage = 'Line of Business <b>' + currentLOB?.description + '</b>  is not activated for this client. Do you want to proceed anyway?';
  
              const result = await this.dialogService.confirmDialog('Inactive Line of Businesss', errMessage);
              if (result === DialogResultType.No) {
                this.navigateOnError(errMessage);
                return;
              }
            }
            
            this.workOrderCreate();
        });
    }
    
    workOrderCreate() {
        this.workOrderCreateInProgress = true;
        this.commonService.logSuccess('Work Order Creation in Progress');
        
        const command = {
            UserProfileIdWorker: this.inputFormGroup.value.UserProfileIdWorker,
            OrganizationIdClient: this.inputFormGroup.value.OrganizationIdClient,
            AtsSourceId: this.sourceId,
            AtsPlacementId: this.placementId,
            LineOfBusiness: this.inputFormGroup.value.LineOfBusinessId,
            TemplateId: this.inputFormGroup.value.TemplateId,
            UiVersion: PhxConstants.UIVersion.Legacy
        };

        this.workorderService
            .workOrderNew(command)
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe({
                next: (workOrderNewResponseSucces: any) => {
                    if (workOrderNewResponseSucces.EntityId) {
                        this.router.navigate(['/next', 'workorder', 0, 0, workOrderNewResponseSucces.EntityId, 'core']);
                    }
                },
                error: (responseError) => {
                    this.ValidationMessages = this.commonService.responseErrorMessages(responseError, null);
            
                    // Create enhanced error message combining generic message with validation details
                    const baseMessage = 'Work Order Creation Failed';
                    const validationDetail = getValidationSummary(this.ValidationMessages);
                    const fullMessage = validationDetail ? `${baseMessage}: ${validationDetail}` : baseMessage;
                    
                    this.commonService.logError(fullMessage);
                    this.workOrderCreateInProgress = false;
                }
            });
    }

    onInitSetup(atsDto: ApplicationTransactionSystemDto) {

        this.inputFormGroup = this.formGroupWorkorderSetup(atsDto);
        this.getListOrganizationClient();
        this.updateFilteredTemplates();

        this.inputFormGroup.controls.OrganizationIdClient.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.isDestroyed$))
            .subscribe(() => {
                this.updateFilteredTemplates();
                this.updateFilteredWorkers();
            });

        // Attach listener
        this.inputFormGroup.controls.UserProfileIdWorker.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.isDestroyed$)
        ).subscribe(x => {
            this.setselectedProfileWorker(x);
            this.updateFilteredTemplates();
        });

        // Attach template listener for worker filtering
        this.inputFormGroup.controls.TemplateId.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.isDestroyed$)
        ).subscribe(() => {
            this.setSelectedTemplate(this.inputFormGroup.get('TemplateId').value);
            this.updateFilteredWorkers();
        });
    }

    formGroupWorkorderSetup(atsDto: ApplicationTransactionSystemDto) {
        const responseError = { ValidationMessages: [] };
        const errFields = [];

        if (!atsDto.AtsPlacement.StartDate) {
            errFields.push('Start Date');
        }

        if (!atsDto.AtsPlacement.EndDate) {
            errFields.push('End Date');
        }

        if (errFields.length > 0) {
            const errMessage = 'Invalid ' + errFields.join(', ') + ' from ATS';

            responseError.ValidationMessages.push({ PropertyName: '', Message: errMessage });
            this.ValidationMessages = responseError;
        }

        this.isRegularPlacement = atsDto.AtsPlacement.LineOfBusinessId === this.phxConstants.LineOfBusiness.Regular
          || atsDto.AtsPlacement.LineOfBusinessId === this.phxConstants.LineOfBusiness.SubVendorPlacement;
        this.atsOwnerLabel = this.isRegularPlacement ? 'Primary Owner' : 'Owner';

        const formGroup = new FormGroup<ToTypedFormGroup<CreateFromAtsForm>>({
                OrganizationIdClient: new FormControl(atsDto.SuggestedOrganizationIdClient || null, [Validators.required]),
                UserProfileIdWorker: new FormControl(atsDto.SuggestedUserProfileIdWorker || null, [Validators.required]),
                LineOfBusinessId: new FormControl(atsDto.AtsPlacement.LineOfBusinessId || null),
                TemplateId: new FormControl(null)
            });

        return formGroup;
    }

    private setSelectedTemplate(templateId: number): void {
        this.selectedTemplate = this.listFilteredWorkOrderTemplates.find(t => t.Id === templateId);
    }

    private setselectedProfileWorker(profileId: number): any {

        this.selectedProfileWorker = this.listUserProfileWorker.find(i => i.Id === profileId);

        if (this.selectedProfileWorker) {
            this.selectedProfileWorkerUrlLink = `/next/contact/${this.selectedProfileWorker.Contact.Id}/profile/`;
            this.selectedProfileWorkerUrlLink += `${this.commonService.getUserProfileTypeSufix(this.selectedProfileWorker.ProfileTypeId)}/${this.selectedProfileWorker.Id}`;
        }
        return null;
    }

    private getCodeValueListsStatic() {
        this.listProfileType = this.codeValueService.getCodeValues(this.codeValueGroups.ProfileType, true);
    }

    private getListOrganizationClient() {
        const isMappedClient = this.atsDto.MappedOrganizationIdClients?.length > 0;

        this.listObservableService
            .listDetailedOrganizationClients$()
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe((response) => {
                if (response) {
                    this.listOrganizationClient = response
                        .filter(i => isMappedClient ? this.atsDto.MappedOrganizationIdClients.includes(i.Id) : true)
                        .map(i => ({
                            ...i,
                            DisplayText: i.Id + ' - ' + i.DisplayText
                        }));
                }
            });
    }

    private getListUserProfileWorker() {
        return this.listObservableService.listUserProfileWorkers$().pipe(
            takeUntil(this.isDestroyed$),
            map(response => {
                if (!response) {
                    return [];
                }
                
                const listUserProfileWorker = this.getFilteredListUserProfileWorker(response);
                listUserProfileWorker.forEach((item: any) => {
                    const profileName = this.listProfileType.find(i => i.id === item.ProfileTypeId);
                    const displayItem = item.Id + '-' + item.Contact.FullName + '-' + profileName.description;
                    item.OrganizationName = item.Organization ? item.Organization.DisplayName : null;
                    if (item.LastModifiedDatetime) {
                        item.LastModifiedDatetime = moment(item.LastModifiedDatetime).format('MMM DD YYYY');
                    }
                    item.DisplayValue = displayItem;
                });

                return listUserProfileWorker;
            })
        );
    }

    private getFilteredListUserProfileWorker(items: UserProfileWorkerListItem[]) {
        // 15634 - workers belonging to certain profileTypes should not appear if they are missing an org
        return items.filter(item => item.ProfileTypeId === PhxConstants.UserProfileType.WorkerCanadianInc
            || item.ProfileTypeId === PhxConstants.UserProfileType.WorkerSubVendor
            || item.ProfileTypeId === PhxConstants.UserProfileType.WorkerUnitedStatesLLC
            ? item.OrganizationId != null
            : true);
    }

    private getTemplatesByEntityTypeId() {
        this.workorderService
            .getTemplatesByEntityTypeId(this.phxConstants.EntityType.Assignment).pipe(
                takeUntil(this.isDestroyed$))
            .subscribe(result => {
                this.listWorkOrderTemplates = result.Items;
                this.updateFilteredTemplates();
            });
    }

    private updateFilteredTemplates() {
        if (!this.inputFormGroup) {
            return;
        }

        const organizationIdClient = this.inputFormGroup.get('OrganizationIdClient').value;
        const lineOfBusiness = this.inputFormGroup.get('LineOfBusinessId').value;
        const currentTemplateId = this.inputFormGroup.value.TemplateId;

        const result = filterWorkOrderTemplates({
            listWorkOrderTemplates: this.listWorkOrderTemplates,
            organizationIdClient,
            lineOfBusinessId: lineOfBusiness,
            selectedProfileWorker: this.selectedProfileWorker,
            codeValueService: this.codeValueService,
            currentTemplateId
        });

        this.listFilteredWorkOrderTemplates = result.filteredTemplates;
        
        if (result.shouldClearTemplate) {
            this.inputFormGroup.controls.TemplateId.setValue(null, { emitEvent: false });
        }
    }

    private updateFilteredWorkers() {
        const organizationIdClient = this.inputFormGroup?.get('OrganizationIdClient')?.value;
        const templateId = this.inputFormGroup?.get('TemplateId')?.value;

        const result = filterWorkOrderWorkers({
            allUserProfileWorkers: this.allUserProfileWorkers,
            organizationIdClient,
            templateId,
            selectedTemplate: this.selectedTemplate,
            codeValueService: this.codeValueService
        });

        this.listUserProfileWorker = result.filteredWorkers;

        if (result.shouldClearWorkerSelection) {
            this.selectedProfileWorker = null;
            this.selectedProfileWorkerUrlLink = null;
            this.inputFormGroup.get('UserProfileIdWorker').setValue(null, { emitEvent: false });
        }
    }
}
